import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Container,
    Stack,
    TextField,
    Switch,
    FormControlLabel,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Tooltip,
    InputAdornment, // Added for link type selection
    Select, // Added for link type selection
    MenuItem, // Added for link type selection
} from '@mui/material';
import {
    Close as CloseIcon,
    Add as AddIcon, // For adding new links
    Link as LinkIcon, // Icon for links
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useNotification } from '../notification/NotificationProvider';

// --- Helper function for image compression (from your YouKnowFormScreen) ---
const compressImage = (file, maxSizeKb = 20) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                const maxDim = 1200;
                if (width > maxDim || height > maxDim) {
                    if (width > height) {
                        height *= maxDim / width;
                        width = maxDim;
                    } else {
                        width *= maxDim / height;
                        height = maxDim;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                let quality = 0.9;
                let dataUrl = canvas.toDataURL('image/jpeg', quality);
                let fileSize = dataUrl.length * (3 / 4) - 2;

                while (fileSize > maxSizeKb * 1024 && quality > 0.1) {
                    quality -= 0.1;
                    dataUrl = canvas.toDataURL('image/jpeg', quality);
                    fileSize = dataUrl.length * (3 / 4) - 2;
                }

                resolve(dataUrl);
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    });
};

const UserProfileSettings = () => {
    const { currentUser, dbUser, isLoading: authLoading } = useAuth();
    const { triggerNotification } = useNotification();
    const navigate = useNavigate();

    // Estado para los datos del perfil
    const [profileData, setProfileData] = useState({
        profilePicture: '', // Data URL de la imagen de perfil
        bio: '',
        openToGroupOffers: false,
        isProfilePublic: true,
        socialLinks: [], // Array de objetos { type: 'facebook', url: '...' }
    });

    const fileInputRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [newLinkType, setNewLinkType] = useState('website');
    const [newLinkUrl, setNewLinkUrl] = useState('');

    useEffect(() => {
        if (!authLoading && dbUser) {
            // Cargar los datos del perfil del usuario de dbUser cuando esté disponible
            setProfileData({
                profilePicture: dbUser.profilePicture || '',
                bio: dbUser.bio || '',
                openToGroupOffers: dbUser.openToGroupOffers || false,
                isProfilePublic: typeof dbUser.isProfilePublic === 'boolean' ? dbUser.isProfilePublic : true,
                socialLinks: dbUser.socialLinks || [],
            });
        }
    }, [dbUser, authLoading]);

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setProfileData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    // --- Image Handling Functions (Adaptadas para una sola foto de perfil) ---
    const handleProfilePictureChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.type.startsWith('image/')) {
                try {
                    setLoading(true);
                    const compressedDataUrl = await compressImage(file, 250); // Ajusta el tamaño si es necesario
                    setProfileData((prevData) => ({
                        ...prevData,
                        profilePicture: compressedDataUrl,
                    }));
                    setErrorMessage('');
                } catch (error) {
                    console.error("Error compressing image:", error);
                    setErrorMessage('Falló el procesamiento de la imagen. Por favor, intenta con otra.');
                } finally {
                    setLoading(false);
                }
            } else {
                setErrorMessage('Solo se permiten archivos de imagen.');
            }
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = ''; // Limpiar el input para permitir subir la misma imagen de nuevo
        }
    };

    const handleRemoveProfilePicture = () => {
        setProfileData((prevData) => ({
            ...prevData,
            profilePicture: '',
        }));
    };

    // --- Social Links Handling Functions ---
    const handleAddLink = () => {
        if (newLinkUrl.trim() && newLinkType) {
            setProfileData((prevData) => ({
                ...prevData,
                socialLinks: [...prevData.socialLinks, { type: newLinkType, url: newLinkUrl.trim() }],
            }));
            setNewLinkType('website'); // Resetear a tipo por defecto
            setNewLinkUrl(''); // Limpiar el campo URL
            setErrorMessage('');
        } else {
            setErrorMessage('Por favor, ingresa una URL y selecciona un tipo de enlace.');
        }
    };

    const handleDeleteLink = (indexToDelete) => {
        setProfileData((prevData) => ({
            ...prevData,
            socialLinks: prevData.socialLinks.filter((_, index) => index !== indexToDelete),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            if (!dbUser) {
                throw new Error('User data not loaded.');
            }
            // Datos a enviar al backend
            const updateData = {
                profilePicture: profileData.profilePicture,
                bio: profileData.bio,
                openToGroupOffers: profileData.openToGroupOffers,
                isProfilePublic: profileData.isProfilePublic,
                socialLinks: profileData.socialLinks,
            };

            // Llama a la función de tu API para actualizar el perfil del usuario
            // Asumo que tienes una función en tu `dbUser` o en `MisBolosApi` para esto.
            // Por ejemplo: await dbUser.updateProfile(updateData);
            // Si la API es similar a `createGroup`, podría ser algo como:
            const response = await dbUser.updateUserProfile(updateData); // Reemplaza `updateUser` con tu función real

            if (response && response.success) { // Asumiendo que la API devuelve un `success` o similar
                setSuccessMessage(response.message || 'Perfil actualizado con éxito.');
                triggerNotification({
                    message: `Has actualizado tu perfil`,
                    sound: true,
                    show: true,
                    type: 'tiny-top',
                    cat: 'received-request-from-group',
                    forceId: 'profile-handle-submit-success'
                });
                // Opcional: navegar a otra página o recargar datos del usuario si es necesario
                // navigate('/profile');
                // window.location.reload(); // Recargar la página para reflejar los 
                await dbUser.update();
                navigate('/misbolos/')
            } else {
                throw new Error(response.message || 'Falló la actualización del perfil.');
            }

        } catch (error) {
            console.error('Error al actualizar el perfil:', error);
            setSuccessMessage('');
            setErrorMessage(error.message || 'Ocurrió un error inesperado al guardar el perfil.');
            triggerNotification({
                message: `No se ha podido actualizar tu perfil`,
                sound: true,
                show: true,
                type: 'tiny-top',
                cat: 'received-request-from-group',
                isError: true,
                forceId: 'profile-handle-submit-error'
            });
        } finally {
            setLoading(false);
        }
    };

    if (authLoading || !dbUser) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            id="user-profile-settings"
            sx={{
                minHeight: '100vh',
                ////background: 'linear-gradient(to right, #f8fafc, #e0f2fe)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
            }}
        >
            <Container maxWidth="sm">
                <Stack spacing={4} alignItems="center" textAlign="center">
                    <Typography variant="h3" fontWeight="bold">
                        Configuración de Perfil
                    </Typography>

                    <Typography variant="h6" color="text.secondary">
                        Actualiza la información de tu perfil.
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', mt: 3, p: 3, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: 'white', boxShadow: 3 }}>
                        <Stack spacing={3}>
                            {/* Sección de Foto de Perfil */}
                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Foto de Perfil</Typography>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleProfilePictureChange}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 2 }}>
                                {profileData.profilePicture ? (
                                    <Box sx={{ position: 'relative', width: 150, height: 150, borderRadius: '50%', overflow: 'hidden', border: '2px solid #ddd' }}>
                                        <img
                                            // src={profileData.profilePicture}
                                            src={`https://bluejims.com:5005/misbolos/image/${profileData.profilePicture}`}
                                            alt="Foto de Perfil"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={handleRemoveProfilePicture}
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                backgroundColor: 'rgba(255,255,255,0.7)',
                                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                                p: '2px',
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    <Box sx={{ width: 150, height: 150, borderRadius: '50%', border: '2px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa' }}>
                                        <Typography variant="caption">No Photo</Typography>
                                    </Box>
                                )}
                                <Button
                                    variant="outlined"
                                    onClick={() => fileInputRef.current.click()}
                                    disabled={loading}
                                >
                                    {profileData.profilePicture ? 'Cambiar Foto' : 'Subir Foto'}
                                </Button>
                            </Box>

                            {/* Campo de Biografía */}
                            <TextField
                                label="Biografía"
                                name="bio"
                                value={profileData.bio}
                                onChange={handleChange}
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                placeholder="Cuéntanos un poco sobre ti..."
                            />

                            {/* Switches de Configuración */}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={profileData.openToGroupOffers}
                                        onChange={handleChange}
                                        name="openToGroupOffers"
                                        color="primary"
                                    />
                                }
                                label="Abierto a ofertas de grupos"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={profileData.isProfilePublic}
                                        onChange={handleChange}
                                        name="isProfilePublic"
                                        color="primary"
                                    />
                                }
                                label="Perfil Público"
                            />

                            {/* Sección de Redes Sociales (Links Indefinidos) */}
                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Redes Sociales y Enlaces</Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Select
                                    value={newLinkType}
                                    onChange={(e) => setNewLinkType(e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 120 }}
                                >
                                    <MenuItem value="website">Web</MenuItem>
                                    <MenuItem value="facebook">Facebook</MenuItem>
                                    <MenuItem value="instagram">Instagram</MenuItem>
                                    <MenuItem value="twitter">X (Twitter)</MenuItem>
                                    <MenuItem value="youtube">YouTube</MenuItem>
                                    <MenuItem value="spotify">Spotify</MenuItem>
                                    <MenuItem value="bandcamp">Bandcamp</MenuItem>
                                    <MenuItem value="soundcloud">SoundCloud</MenuItem>
                                    <MenuItem value="tiktok">TikTok</MenuItem>
                                    <MenuItem value="other">Otro</MenuItem>
                                </Select>
                                <TextField
                                    label="URL del Enlace"
                                    value={newLinkUrl}
                                    onChange={(e) => setNewLinkUrl(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    placeholder="https://ejemplo.com/mi-perfil"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LinkIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault(); // Previene el envío del formulario
                                            handleAddLink();
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleAddLink}
                                    sx={{ minWidth: 'auto', p: '8px' }} // Ajusta el padding para un botón más pequeño
                                >
                                    <AddIcon />
                                </Button>
                            </Stack>
                            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {profileData.socialLinks.map((link, index) => (
                                    <Chip
                                        key={index}
                                        label={`${link.type}: ${link.url.length > 30 ? link.url.substring(0, 27) + '...' : link.url}`}
                                        onDelete={() => handleDeleteLink(index)}
                                        color="info"
                                        clickable
                                        component="a"
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        icon={<LinkIcon />}
                                    />
                                ))}
                            </Box>


                            {/* Mensajes de feedback */}
                            {successMessage && (
                                <Alert severity="success">{successMessage}</Alert>
                            )}
                            {errorMessage && (
                                <Alert severity="error">{errorMessage}</Alert>
                            )}

                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                size="large"
                                fullWidth
                                disabled={loading}
                                sx={{
                                    borderRadius: 2,
                                    paddingY: 1.5,
                                }}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Guardar Cambios'}
                            </Button>
                        </Stack>
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
};

export default UserProfileSettings;